
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { KitImageConfig } from "@/types/ticker";

// Define the shape of the raw data from Supabase
interface RawKitImageConfig {
  id: string;
  image_id: string;
  duration: number;
  is_active: boolean;
  alert_image: {
    storage_path: string;
  } | null;
}

export function useKitImages(kitId: string | null) {
  const { data: kitImageConfigs = [], refetch } = useQuery<KitImageConfig[]>({
    queryKey: ['kit-image-configs-with-images', kitId],
    queryFn: async () => {
      if (!kitId) {
        console.log('No kitId provided');
        return [];
      }
      
      console.log('Fetching kit configurations for kit:', kitId);
      
      // First, let's check what image configs exist
      const { data: configsOnly, error: configError } = await supabase
        .from('kit_image_configs')
        .select('*')
        .eq('kit_id', kitId);
      
      console.log('Raw kit configs:', configsOnly);
      
      if (configError) {
        console.error('Error fetching configs:', configError);
        throw configError;
      }

      // Now let's try the join
      const { data, error } = await supabase
        .from('kit_image_configs')
        .select(`
          id,
          image_id,
          duration,
          is_active,
          alert_image:alert_images(storage_path)
        `)
        .eq('kit_id', kitId)
        .order('display_order', { ascending: true });
      
      if (error) {
        console.error('Error fetching joined data:', error);
        throw error;
      }
      
      console.log('Full joined data:', data);
      
      // Cast and transform the data
      const typedData = data as unknown as RawKitImageConfig[];
      const mappedData: KitImageConfig[] = typedData
        .filter(config => config.alert_image !== null) // Filter out configs with no alert image
        .map(config => ({
          id: config.id,
          image_id: config.image_id,
          duration: config.duration,
          is_active: config.is_active,
          storage_path: config.alert_image?.storage_path || '',
        }))
        .filter(config => config.storage_path !== ''); // Filter out any remaining empty storage paths

      console.log('Final mapped data:', mappedData);
      return mappedData;
    },
    enabled: !!kitId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 1000, // Poll every second as a backup
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!kitId) return;

    console.log('Setting up real-time subscription for kit:', kitId);
    
    // Create a unique channel name for each kit
    const channel = supabase
      .channel(`kit-image-changes-${kitId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'kit_image_configs',
          filter: `kit_id=eq.${kitId}`,
        },
        (payload) => {
          console.log('Received kit_image_configs update:', payload);
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'alert_images',
        },
        (payload) => {
          console.log('Received alert_images update:', payload);
          refetch();
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    // Cleanup subscription when component unmounts or kitId changes
    return () => {
      console.log('Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [kitId, refetch]);

  // Log whenever the data changes
  console.log('Current kitImageConfigs:', kitImageConfigs);

  return { kitImageConfigs };
}
